import React, { useContext, useEffect, useState, useRef } from 'react';
import { PIPRedirector, DynamicComponent, AppContext } from '@thd-nucleus/app-render';
import { Link, Accordion, AccordionBody, AccordionHeader } from '@one-thd/sui-atomic-components';
import {
  useDataModel,
  QueryContext,
  QueryProvider,
  extend,
  alias,
  params,
  shape as shapeType,
  string as stringType,
  bool as boolType
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStoreId, useConfigService } from '@thd-nucleus/experience-context';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import {
  Col,
  Row,
  Placeholder,
} from '@thd-olt-component-react/core-ui';
import { Discontinued } from '@thd-olt-component-react/discontinued';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { Hydrator, declareContexts } from '@thd-olt-component-react/hydrator';
import { MediaGalleryDesktop, MediaGalleryCarousel } from '@thd-olt-component-react/media-gallery';
import { Metadata, ProductMetadata } from '@thd-olt-component-react/metadata';
import { ProExtraProductOnly } from '@thd-olt-component-react/pro-extra-product-only';
import { ProductDetails } from '@thd-olt-component-react/product-details';
import { ProductInfoBar } from '@thd-olt-component-react/product-info-bar';
import { ProductOverviewAccordionTitle } from '@thd-olt-component-react/product-overview';
import { QuestionsAndAnswersAccordionTitle } from '@thd-olt-component-react/questions-and-answers';
import { RatingsAndReviewsAccordionTitle } from '@thd-olt-component-react/ratings-and-reviews';
import { RelatedSearch } from '@thd-olt-component-react/related-search';
import { useParams } from '@thd-olt-component-react/router';
import { SalientListItem, SalientPoints } from '@thd-olt-component-react/salient-points';
import { SpecificationsAccordionTitle } from '@thd-olt-component-react/specifications';
import { RetailMediaCarousel } from '@thd-olt-component-react/retail-media-pla-sponsored-placements';
import { CustomerContext } from '@thd-olt-functional/customer-information';
import { DynamicRecsWrapper, RecentlyViewedWrapper } from '@thd-olt-component-react/thd-recs-containers';
import { Sharebelt, ShareBeltPrint, ShareBeltShareButton, ShareFavorite } from '@thd-olt-component-react/thd-sharebelt';

import {
  QuestionsAndAnswersAccordionBody,
  QuestionsAndAnswersAccordionBodyLoad,
  QuestionsAndAnswersAccordionBodyModel,
  RatingsAndReviewsAccordionBody,
  RatingsAndReviewsAccordionBodyLoad,
  RatingsAndReviewsAccordionBodyModel,
  SpecificationsAccordionBody,
  SpecificationsAccordionBodyLoad,
  SpecificationsAccordionBodyModel,
  ProductOverviewAccordionBody,
  ProductOverviewAccordionBodyLoad,
  ProductOverviewAccordionBodyModel,
} from './dynamic-imports';
import {
  shouldDisplayReferrerRecommendations,
  shouldDisplayVisuallySimilar
} from './product-utils';
import '../styles/product-page.scss';
import ZoneCard from '../utils/zone-card';
import { RECS_REQUEST_KEY, HYDRATION_STANDARD_SCROLL_BUFFER } from '../constants';

export const Product = () => {
  declareContexts([QueryContext, ExperienceContext, CustomerContext]);
  const { channel } = useContext(ExperienceContext);
  const qctx = useContext(QueryContext);
  const { instance } = useContext(AppContext);
  const thdSeoBotDetected = instance?.thdSEOBotDetection;
  const fsFilmstripUserImages = useConfigService('fs-prop:filmstrip-user-images-ab');
  const { itemId } = useParams();
  const storeId = useStoreId();
  const isMobile = channel === 'mobile';

  const { data: productData } = useDataModel('clientOnlyProduct', {
    variables: {
      itemId,
      storeId
    },
    skip: !itemId,
    ssr: false
  });

  const productOverviewRef = useRef(null);
  const specificationsRef = useRef(null);
  const questionsRef = useRef(null);
  const ratingsRef = useRef(null);

  const [mounted, setMounted] = useState(false);
  const [expanded, setExpanded] = useState('');

  const anchorImage = productData?.product?.media?.image?.url;

  const handleAccordionChange = (accordion, canCloseAccordion = true) => {
    if (!expanded[accordion] || canCloseAccordion) {
      setExpanded((prevState) => ({ ...prevState, [accordion]: !prevState[accordion] }));
    }
  };

  const scrollTo = (offset, callback) => {
    const fixedOffset = offset.toFixed();
    const onScroll = () => {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener('scroll', onScroll);
        callback();
      }
    };
    window.addEventListener('scroll', onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: 'smooth',
    });
  };

  const handleProductDetailsClick = (accordion) => {
    handleAccordionChange(accordion, false);
  };

  const handleViewMoreDetails = () => {
    const offset = document.getElementById('product-section-overview').offsetTop;
    scrollTo(offset, () => handleAccordionChange('#product-section-overview', false));
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <>
      <ErrorBoundary name="redirector">
        <PIPRedirector disablePEPRedirect disableCanonicalRedirect disableCMMMCRedirect />
      </ErrorBoundary>
      <ErrorBoundary name="product-metadata">
        <Metadata>
          <ProductMetadata itemId={itemId} />
        </Metadata>
      </ErrorBoundary>
      <Row className="isBound">
        <Row className="u--paddingTop u--paddingBottom">
          <Col sm="7" flatten>
            <ErrorBoundary name="breadcrumbs">
              <Breadcrumbs
                itemId={itemId}
                showInternet={isMobile}
              />
            </ErrorBoundary>
          </Col>
          <Col sm="5" nopadding>
            {!isMobile
              && (
                <div className="product-info--end">
                  <ErrorBoundary name="product-info-bar">
                    <ProductInfoBar itemId={itemId} />
                  </ErrorBoundary>
                </div>
              )}
          </Col>
        </Row>
        {mounted && shouldDisplayReferrerRecommendations() && (
          <Row id="recs:organic_search">
            <Col nopadding>
              <ErrorBoundary name="recs:organic_search">
                <DynamicRecsWrapper
                  schemaName="pipsem"
                  anchorId={itemId}
                  errorBoundary
                  hideATC
                  requestKey={RECS_REQUEST_KEY}
                  hydrator={{
                    className: 'grid',
                    tag: ZoneCard,
                    delay: 0,
                    scrollBuffer: 0,
                    id: 'pipsem'
                  }}
                />
              </ErrorBoundary>
            </Col>
          </Row>
        )}
        <Row>
          <Col nopadding>
            <Row>
              <Col>
                <ErrorBoundary name="discontinued">
                  <Discontinued itemId={itemId} />
                </ErrorBoundary>
              </Col>
              <Col sm="7" flatten className="sticky">
                <div className="zone-card overflow">
                  <Col sm="12">
                    <ErrorBoundary name="product-details">
                      <ProductDetails itemId={itemId}>
                        <ProductDetails.Badge itemId={itemId} />
                        <ProductDetails.BrandCollection showCollection showBrandHyperLink />
                        <div className="product-details__badge-title--wrapper">
                          <ProductDetails.Title />
                        </div>
                        <div className="product-details__favorites">
                          <Sharebelt itemId={itemId}>
                            <ShareFavorite showCircle showFavoritesCount />
                          </Sharebelt>
                        </div>
                        <ProductDetails.Ratings
                          showBadge
                          target="#product-section-rr"
                          offsetselector="#sticky-nav"
                          qaselector="#product-section-qa"
                          onClick={handleProductDetailsClick}
                        />
                      </ProductDetails>
                    </ErrorBoundary>
                  </Col>
                  <Col sm="12">
                    <ErrorBoundary name="media-gallery">
                      {
                        !isMobile && (
                          <MediaGalleryDesktop itemId={itemId} highRes showLoader mainImageFetchPriority="high" />
                        )
                      }
                      {
                        isMobile && (
                          <MediaGalleryCarousel
                            itemId={itemId}
                            overlayType="filmstrip"
                            showLoader
                            showUserImages={fsFilmstripUserImages}
                            firstItemFetchPriority="high"
                          />
                        )
                      }
                    </ErrorBoundary>
                  </Col>
                  <Row>
                    <Col className="u__text-align--center">
                      <ErrorBoundary name="sharebelt">
                        <Sharebelt horizontal itemId={itemId}>
                          <ShareBeltShareButton email />
                          <ShareBeltPrint />
                        </Sharebelt>
                      </ErrorBoundary>
                    </Col>
                  </Row>
                </div>
              </Col>
              <Col sm="5" flatten>
                <div className="zone-card">
                  <Row>
                    {!isMobile
                      && (
                        <Col>
                          <ErrorBoundary name="salient">
                            <SalientPoints itemId={itemId} max={3}>
                              <SalientListItem>
                                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                                <Link
                                  underline="always"
                                  onClick={handleViewMoreDetails}
                                >
                                  View More Details
                                </Link>
                              </SalientListItem>
                            </SalientPoints>
                          </ErrorBoundary>
                        </Col>
                      )}
                    <Col>
                      <ErrorBoundary name="pro-extra">
                        <ProExtraProductOnly />
                      </ErrorBoundary>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
        <ErrorBoundary name="collapsible-sections">
          <DynamicComponent
            pageType="pip"
          >
            <Hydrator
              id="collapsible-accordion"
              className="grid"
              placeholder={(
                <>
                  <div
                    ref={productOverviewRef}
                    id="product-section-overview"
                    className="accordion-placeholder"
                  >
                    <Placeholder width="100%" height="82px" color="#F5F5F5" />
                  </div>
                  <div
                    ref={specificationsRef}
                    id="product-section-key-feat"
                    className="accordion-placeholder"
                  >
                    <Placeholder width="100%" height="82px" color="#F5F5F5" />
                  </div>
                  <div
                    ref={questionsRef}
                    id="product-section-qa"
                    className="accordion-placeholder"
                  >
                    <Placeholder width="100%" height="82px" color="#F5F5F5" />
                  </div>
                  <div
                    ref={ratingsRef}
                    id="product-section-rr"
                    className="accordion-placeholder"
                  >
                    <Placeholder width="100%" height="82px" color="#F5F5F5" />
                  </div>
                </>
              )}
            >
              <div className="sui-flex sui-flex-col sui-w-full">
                <div
                  ref={productOverviewRef}
                  id="product-section-overview"
                  className="product-section-overview"
                  name="product-section-overview"
                >
                  <Accordion
                    key="product-details-accordion"
                    expanded={expanded['#product-section-overview'] || thdSeoBotDetected}
                    onChange={() => handleAccordionChange('#product-section-overview')}
                  >
                    <ErrorBoundary name="product-overview-accordion-title">
                      <AccordionHeader>
                        <ProductOverviewAccordionTitle />
                      </AccordionHeader>
                    </ErrorBoundary>
                    <ErrorBoundary name="product-overview-accordion-body">
                      <AccordionBody>
                        <DynamicComponent pageType="pip">
                          <Hydrator
                            id="product-section-product-overview"
                            className="grid"
                            waitFor={ProductOverviewAccordionBodyLoad}
                            placeholder={(
                              <Placeholder
                                width="100%"
                                height="2000px"
                                color="#F5F5F5"
                              />
                            )}
                          >
                            <ProductOverviewAccordionBody
                              itemId={itemId}
                              showReturnMessage
                              revJet
                              qctx={qctx}
                            />
                          </Hydrator>
                        </DynamicComponent>
                      </AccordionBody>
                    </ErrorBoundary>
                  </Accordion>
                </div>
                <div
                  ref={specificationsRef}
                  id="product-section-key-feat"
                  className="product-section-key-feat"
                >
                  <Accordion
                    key="specifications-accordion"
                    expanded={expanded['#product-section-key-feat'] || thdSeoBotDetected}
                    onChange={() => handleAccordionChange('#product-section-key-feat')}
                  >
                    <ErrorBoundary name="specifications-accordion-title">
                      <AccordionHeader>
                        <div className="sui-flex sui-flex-col">
                          <SpecificationsAccordionTitle itemId={itemId} />
                        </div>
                      </AccordionHeader>
                    </ErrorBoundary>
                    <ErrorBoundary name="specifications-accordion-body">
                      <AccordionBody>
                        <DynamicComponent pageType="pip">
                          <Hydrator
                            id="product-section-specifications"
                            className="grid"
                            waitFor={SpecificationsAccordionBodyLoad}
                            placeholder={(
                              <Placeholder
                                width="100%"
                                height="700px"
                                color="#F5F5F5"
                              />
                            )}
                          >
                            <SpecificationsAccordionBody
                              itemId={itemId}
                              qctx={qctx}
                            />
                          </Hydrator>
                        </DynamicComponent>
                      </AccordionBody>
                    </ErrorBoundary>
                  </Accordion>
                </div>
                <div
                  ref={questionsRef}
                  id="product-section-qa"
                  className="product-section-qa"
                >
                  <Accordion
                    key="questions-answers-accordion"
                    expanded={expanded['#product-section-qa'] || thdSeoBotDetected}
                    onChange={() => handleAccordionChange('#product-section-qa')}
                  >
                    <ErrorBoundary name="questions-answers-accordion-title">
                      <AccordionHeader>
                        <QuestionsAndAnswersAccordionTitle
                          noSpacing
                          itemId={itemId}
                          hydrator={{
                            className: 'grid',
                          }}
                          dynamic={{
                            pageType: 'pip',
                          }}
                        />
                      </AccordionHeader>
                    </ErrorBoundary>
                    <ErrorBoundary name="questions-answers-accordion-body">
                      <AccordionBody>
                        <DynamicComponent pageType="pip">
                          <Hydrator
                            id="product-section-questions-answers"
                            className="grid"
                            waitFor={QuestionsAndAnswersAccordionBodyLoad}
                            placeholder={(
                              <Placeholder
                                width="100%"
                                height="700px"
                                color="#F5F5F5"
                              />
                            )}
                          >
                            <QuestionsAndAnswersAccordionBody
                              itemId={itemId}
                              seoPageNumber={thdSeoBotDetected ? 1 : null}
                              qctx={qctx}
                            />
                          </Hydrator>
                        </DynamicComponent>
                      </AccordionBody>
                    </ErrorBoundary>
                  </Accordion>
                </div>
                <div
                  ref={ratingsRef}
                  id="product-section-rr"
                  className="product-section-rr"
                >
                  <Accordion
                    key="ratings-reviews-accordion"
                    expanded={expanded['#product-section-rr'] || thdSeoBotDetected}
                    onChange={() => handleAccordionChange('#product-section-rr')}
                  >
                    <ErrorBoundary name="ratings-reviews-accordion-title">
                      <AccordionHeader>
                        <RatingsAndReviewsAccordionTitle
                          noSpacing
                          itemId={itemId}
                          hydrator={{
                            className: 'grid'
                          }}
                          dynamic={{
                            pageType: 'pip'
                          }}
                        />
                      </AccordionHeader>
                    </ErrorBoundary>
                    <ErrorBoundary name="ratings-reviews-accordion-body">
                      <AccordionBody>
                        <DynamicComponent pageType="pip">
                          <Hydrator
                            id="product-section-ratings-reviews"
                            className="grid"
                            waitFor={RatingsAndReviewsAccordionBodyLoad}
                            placeholder={(
                              <Placeholder
                                width="100%"
                                height="2000px"
                                color="#F5F5F5"
                              />
                            )}
                          >
                            <RatingsAndReviewsAccordionBody
                              itemId={itemId}
                              qctx={qctx}
                              prosAndCons
                            />
                          </Hydrator>
                        </DynamicComponent>
                      </AccordionBody>
                    </ErrorBoundary>
                  </Accordion>
                </div>
              </div>
            </Hydrator>
          </DynamicComponent>
        </ErrorBoundary>
        {
          shouldDisplayVisuallySimilar(productData) && (
            <Row>
              <ErrorBoundary name="visually-similar">
                <DynamicRecsWrapper
                  schemaName="visuallysimilar_1_0"
                  anchorImage={anchorImage}
                  anchorId={itemId}
                  errorBoundary
                  requestKey={RECS_REQUEST_KEY}
                  hydrator={{
                    className: 'grid',
                    tag: ZoneCard,
                    id: 'visuallysimilar_1_0',
                    scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
                  }}
                />
              </ErrorBoundary>
            </Row>
          )
        }
        <Row>
          <Col>
            <ErrorBoundary name="pip_sponsored">
              <RetailMediaCarousel
                breadCrumbs={productData?.product?.taxonomy?.breadCrumbs}
                pageContext={{
                  schema: 'pip_sponsored',
                  data: { itemId },
                  isPip: true
                }}
                showSponsoredCarousel
                dynamic={{
                  pageType: 'pip'
                }}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  delay: 1500,
                  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
                }}
              />
            </ErrorBoundary>
          </Col>
        </Row>
        <Row>
          <Col>
            <ErrorBoundary name="recently-viewed">
              <QueryProvider cacheKey="rv-pip-recs" persist>
                <RecentlyViewedWrapper
                  schemaName="rv_gm_pip_rr"
                  errorBoundary
                  hydrator={{
                    className: 'grid',
                    tag: ZoneCard,
                    id: 'rv_gm_pip_rr',
                    delay: 2000,
                    scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
                  }}
                />
              </QueryProvider>
            </ErrorBoundary>
          </Col>
        </Row>
        <Row>
          <ErrorBoundary name="related-search">
            <RelatedSearch
              itemId={itemId}
              dynamic={{
                pageType: 'pip'
              }}
              hydrator={{
                className: 'col__12-12 sui-my-4'
              }}
            />
          </ErrorBoundary>
        </Row>
      </Row>
    </>
  );
};

Product.displayName = 'ProductPage';

Product.dataModel = extend({},
  // Object to help with recommendations calls
  {
    clientOnlyProduct: alias('product').params({ itemId: stringType().isRequired() }).shape({
      info: shapeType({
        recommendationFlags: shapeType({
          visualNavigation: boolType()
        })
      })
    })
  },
  Breadcrumbs,
  Discontinued,
  DynamicRecsWrapper,
  MediaGalleryDesktop,
  MediaGalleryCarousel,
  PIPRedirector,
  ProductDetails,
  ProductInfoBar,
  ProductMetadata,
  ProExtraProductOnly,
  RecentlyViewedWrapper,
  RelatedSearch,
  SalientPoints,
  Sharebelt,
  RetailMediaCarousel,
  QuestionsAndAnswersAccordionBodyModel,
  RatingsAndReviewsAccordionBodyModel,
  SpecificationsAccordionBodyModel,
  ProductOverviewAccordionBodyModel
);
